import {
  Accordion,
  AccordionSizeEnum,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  LoaderIcon,
  TagsIcon,
  Tooltip,
  Typography,
  WarningInfoIcon,
} from '@acceso/design-system';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuiv4 } from 'uuid';

import { useCatalogueSelectProps } from '@/components/CatalogueListbox/use-catalogue-select-props';
import { CollapseAllButton } from '@/components/CollapseAllButton/CollapseAllButton';
import { SelectForm } from '@/components/FormFields/SelectForm';
import Listbox from '@/components/Listbox';
import { PresenceGroupCard } from '@/components/PresenceGroupCard/PresenceGroupCard';
import { ThumbnailSizeSlider } from '@/components/ThumbnailSizeSlider/ThumbnailSizeSlider';
import { EPSILON } from '@/constants';
import { TSegmentLogoClip } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitCuts/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { collectOutsideCutLabels } from '@/features/broadcast-schedule-program/helpers/collect-outside-cut-labels';
import { findSegmentByDate } from '@/features/broadcast-schedule-program/helpers/find-segment-by-date';
import { isGroupOutsideCut } from '@/features/broadcast-schedule-program/helpers/is-group-outside-cut';
import { usePatchClipLogo } from '@/features/clips/api/patch-clips-logos';
import { useGetLabels } from '@/features/labels/api/get-labels';
import { seedSelectedLogo, useSelectedLogos } from '@/features/logos/api/get-logos';
import { LogosDrawerForm } from '@/features/logos/components/LogosDrawerForm';
import { useLogoPicker } from '@/features/logos/hooks/use-logo-picker';
import { useLogosLabelsFilterStore } from '@/features/logos/stores/logos-labels-filter';
import { useGetSegmentId } from '@/features/segments/api/get-segment-id';
import { useAddPresencesGrouping } from '@/hooks/use-add-presences-grouping';
import { latestImageOf } from '@/hooks/use-catalogue-picker';
import { useItemThumbnailMap } from '@/hooks/use-item-thumbnail-map';
import { VIDEO_OFFSET_TIME_CONTROL } from '@/hooks/use-presence-preview-box';
import { useSegmentPresences } from '@/hooks/use-segment-presences';
import { useNotificationStore } from '@/stores/notifications';
import { PresenceTypeEnum } from '@/types/presences';
import { convertDatesToSeconds, getAbsoluteDateFromPlayerTime } from '@/utils/hh-mm-ss-into-number';
import { normalizePresentIds } from '@/utils/ids';
import { resolveItemId } from '@/utils/presence-groups';

import { ClipTooltip } from '../ClipTooltip/ClipTooltip';

type AddLogosProps = {
  logos: TSegmentLogoClip[];
  setLogos: Dispatch<SetStateAction<TSegmentLogoClip[]>>;
  disabled?: boolean;
  isLoadingPreview?: boolean;
  showWarningPresences?: boolean;
  cutStart?: Date | null;
  cutEnd?: Date | null;
};

export function AddLogos({
  logos,
  disabled,
  setLogos,
  isLoadingPreview,
  showWarningPresences = false,
  cutStart = null,
  cutEnd = null,
}: AddLogosProps) {
  const {
    activeClip,
    drawerRect,
    drawingFor,
    visibleBox,
    drawerLogo,
    activeSegment,
    segmentItems,
    playerVideoCurrentTime,
    setDrawingFor,
    setVisibleBox,
    setDrawerLogo,
    setDrawerRect,
    setActiveSegment,
    setPendingSeekTime,
    videoControlRef,
  } = useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    isCreatingLogos,
    form,
    drawerLogo: drawerLogoForm,
    handleSubmitLogo,
    renderAdditionalButtons,
  } = useSegmentPresences();
  const { data: labelsData } = useGetLabels();
  const itemThumbnailMap = useItemThumbnailMap(logos, segmentItems);

  const [segmentIdToFetch, setSegmentIdToFetch] = useState<string | null>(null);
  const selectedLabels = useLogosLabelsFilterStore(state => state.labels);
  const setSelectedLabels = useLogosLabelsFilterStore(state => state.setLabels);
  const [isLabelFilterOpen, setIsLabelFilterOpen] = useState(false);
  const [labelDropdownCoords, setLabelDropdownCoords] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const labelFilterRef = useRef<HTMLDivElement | null>(null);
  const { data: fetchedSegment } = useGetSegmentId({ id: segmentIdToFetch || '' });
  const { addWarningNotification } = useNotificationStore();

  useLayoutEffect(() => {
    if (!isLabelFilterOpen || !labelFilterRef.current) return;
    const rect = labelFilterRef.current.getBoundingClientRect();
    setLabelDropdownCoords({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }, [isLabelFilterOpen]);

  const labelItems = useMemo(
    () =>
      (labelsData ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ id, name }) => ({ value: id, label: name })),
    [labelsData]
  );

  const logoPicker = useLogoPicker({ value: form.watch('logo_id'), labelsIn: selectedLabels });
  const logoSelectProps = useCatalogueSelectProps(logoPicker);
  const { findOption } = logoPicker;

  const presentLogoIds = useMemo(
    () => normalizePresentIds(logos.map(logo => logo.logo_id)),
    [logos]
  );
  const { data: presentLogos } = useSelectedLogos(presentLogoIds, { keepPrevious: true });
  const presentLogoById = useMemo(
    () => new Map((presentLogos?.data ?? []).map(logo => [logo.id, logo])),
    [presentLogos]
  );

  const isFilteringByLabel = selectedLabels.length > 0;

  // Each presence is judged by its own resolved logo. One whose logo is not resolved yet, or
  // whose resolution failed, stays shown rather than hidden on a guess.
  const passesLabelFilter = useCallback(
    (logoId: string | null | undefined) => {
      if (!isFilteringByLabel) return true;
      if (!logoId) return false;
      const logo = presentLogoById.get(logoId);
      if (!logo) return true;
      return !!logo.labels?.some(labelId => selectedLabels.includes(labelId));
    },
    [isFilteringByLabel, presentLogoById, selectedLabels]
  );

  useEffect(() => {
    if (fetchedSegment) {
      setActiveSegment(fetchedSegment);
      setSegmentIdToFetch(null);
    }
  }, [fetchedSegment]);

  const {
    groupedItems,
    selectedIds,
    setSelectedIds,
    thumbnailSize,
    setThumbnailSize,
    toggleGroupSelection,
    toggleItemSelection,
  } = useAddPresencesGrouping(logos, 'logo_id');

  const [allCollapsed, setAllCollapsed] = useState(true);

  const patchClipLogo = usePatchClipLogo();

  const handleReviewGroup = useCallback(
    (leafIds: string[], reviewState: 0 | 1) => {
      const items = logos.filter(l => leafIds.includes(resolveItemId(l)) && l.id);
      if (!items.length) return;

      items.forEach((item, i) => {
        patchClipLogo.mutate({
          id: item.id!,
          review_state: reviewState,
          silently: i !== items.length - 1,
        });
      });

      setLogos(prev =>
        prev.map(l =>
          leafIds.includes(resolveItemId(l)) ? { ...l, review_state: reviewState } : l
        )
      );
    },
    [logos, patchClipLogo, setLogos]
  );

  const handleValidateGroup = useCallback(
    (leafIds: string[]) => handleReviewGroup(leafIds, 1),
    [handleReviewGroup]
  );
  const handleInvalidateGroup = useCallback(
    (leafIds: string[]) => handleReviewGroup(leafIds, 0),
    [handleReviewGroup]
  );

  const handlePreviewLogo = useCallback(
    (box: TSegmentLogoClip) => {
      if (!box.parameters) throw new Error('box.parameters is undefined');
      if (!segmentItems) throw new Error('segmentItems is undefined');

      const startDate = box.s_datetime || box.start_datetime;
      const segmentToUpdate = findSegmentByDate(segmentItems, startDate);

      let seconds = 0;
      if (segmentToUpdate) {
        setActiveSegment(segmentToUpdate);
        seconds =
          convertDatesToSeconds(startDate, segmentToUpdate.start_datetime) +
          VIDEO_OFFSET_TIME_CONTROL;
      } else {
        addWarningNotification(
          'No se encontró el segmento para la fecha seleccionada.',
          'Error al buscar segmento'
        );
        return;
      }
      setPendingSeekTime?.(seconds);
      videoControlRef.current?.seekToSecond(seconds);

      setVisibleBox(prevVisibleBox => {
        if (
          prevVisibleBox &&
          prevVisibleBox.bbox.every(
            (val, index) => Math.abs(val - box.parameters?.bbox[index]) < EPSILON
          )
        ) {
          return null;
        }
        return {
          bbox: box.parameters?.bbox,
          entity_label: box.entity_label || '',
          frame: seconds || 0,
          entity_score: box.entity_score || 0,
          id: box.tempId || box?.id,
        };
      });
    },
    [segmentItems, videoControlRef]
  );

  const handleClickAddLogo = useCallback(() => {
    if (drawerLogo && drawingFor === 'logo') {
      setDrawerLogo(false);
      setDrawingFor(null);
      setDrawerRect(null);
      return;
    }
    if (drawerLogo) {
      setDrawingFor('logo');
      return;
    } else {
      setDrawerLogo(true);
      setDrawingFor('logo');
    }
  }, [drawerLogo, drawingFor]);

  const handleChangeSelect = useCallback(
    (value: any) => {
      if (!activeSegment) throw new Error('activeSegment is undefined');

      const newDateTime = getAbsoluteDateFromPlayerTime(
        activeSegment.start_datetime,
        playerVideoCurrentTime
      );

      const chosen = findOption(value);
      const newLogo: any = {
        logo_id: chosen?.option.value,
        tempId: uuiv4(),
        entity_label: chosen?.option.label,
        s_datetime: newDateTime,
        e_datetime: newDateTime,
        image: latestImageOf(chosen),
        addedManually: true,
        parameters: {
          bbox: drawerRect || [0, 0, 0, 0],
          segment_id: activeSegment.id,
        },
        entity_score: 1,
      };
      setLogos(prev => [...prev, newLogo]);
      setDrawerLogo(false);
      setDrawerRect(null);
    },
    [setLogos, drawerRect, findOption, activeSegment, playerVideoCurrentTime]
  );

  const handleBatchDelete = useCallback(
    (leafIds: string[]) => {
      setLogos(prev => {
        return prev.filter(l => !leafIds.includes(resolveItemId(l)));
      });
      setSelectedIds(prev => prev.filter(id => !leafIds.includes(id)));
      setVisibleBox(prev => (prev && leafIds.includes(String(prev.id ?? '')) ? null : prev));
    },
    [setLogos, setSelectedIds, setVisibleBox]
  );

  const handleBatchMove = useCallback(
    (targetLogoId: string, leafIds: string[]) => {
      const target = findOption(targetLogoId);
      setLogos(prev =>
        prev.map(l =>
          leafIds.includes(resolveItemId(l))
            ? {
                ...l,
                logo_id: targetLogoId,
                entity_label: target?.option.label ?? l.entity_label,
                image: latestImageOf(target) || l.image,
              }
            : l
        )
      );
      setSelectedIds(prev => prev.filter(id => !leafIds.includes(id)));
    },
    [setLogos, setSelectedIds, findOption]
  );

  // Refreshing data when clip change
  useEffect(() => {
    form.resetField('logo_id');
  }, [activeClip?.id]);

  const visibleGroupedItems = useMemo(() => {
    if (!isFilteringByLabel) return groupedItems;
    return (groupedItems as any[]).filter(group => {
      const leaves = (group as any).children ?? [group];
      return leaves.some((leaf: any) => passesLabelFilter(leaf?.logo_id));
    });
  }, [groupedItems, isFilteringByLabel, passesLabelFilter]);

  const hiddenOutsideCutLabels = useMemo(() => {
    if (visibleGroupedItems === groupedItems) return [];

    const visible = new Set(visibleGroupedItems as any[]);
    const hidden = (groupedItems as any[]).filter(group => !visible.has(group));

    return collectOutsideCutLabels<TSegmentLogoClip>(hidden, cutStart, cutEnd);
  }, [groupedItems, visibleGroupedItems, cutStart, cutEnd]);

  // The label filter can hide the very presence that blocks the save, leaving the
  // reviewer with a warning and no red card to act on. Only speak up once nothing
  // visible is marked: while a red card is on screen there is already work to do.
  const showHiddenOutsideCutWarning = useMemo(
    () =>
      showWarningPresences &&
      !visibleGroupedItems.some(group =>
        isGroupOutsideCut<TSegmentLogoClip>(group as any, cutStart, cutEnd)
      ),
    [showWarningPresences, visibleGroupedItems, cutStart, cutEnd]
  );

  const visibleLogosCount = useMemo(
    () => logos.filter(l => passesLabelFilter(l.logo_id)).length,
    [logos, passesLabelFilter]
  );

  const handleSubmitLogoWrapper = useCallback(
    async (data: any) => {
      const createdLogo = await handleSubmitLogo(data);

      if (createdLogo) {
        seedSelectedLogo(queryClient, createdLogo);
        form.setValue('logo_id', createdLogo.id);
      }

      if (createdLogo && activeSegment && drawerRect) {
        const newDateTime = getAbsoluteDateFromPlayerTime(
          activeSegment.start_datetime,
          playerVideoCurrentTime
        );

        const newLogo: any = {
          logo_id: createdLogo.id,
          tempId: uuiv4(),
          entity_label: createdLogo.name,
          s_datetime: newDateTime,
          e_datetime: newDateTime,
          image: createdLogo.parameters?.main_logo_presigned_url,
          addedManually: true,
          parameters: {
            bbox: drawerRect,
            segment_id: activeSegment.id,
          },
          entity_score: 1,
        };
        setLogos(prev => [...prev, newLogo]);
        setDrawerLogo(false);
        setDrawerRect(null);
      }

      return createdLogo;
    },
    [
      handleSubmitLogo,
      queryClient,
      form,
      activeSegment,
      drawerRect,
      playerVideoCurrentTime,
      setLogos,
      setDrawerLogo,
      setDrawerRect,
    ]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'l') {
        event.preventDefault();
        handleClickAddLogo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClickAddLogo]);

  const getLabelsFilter = () => {
    if (labelItems.length === 0) return null;

    return (
      <div ref={labelFilterRef} className="min-w-[220px]">
        <Accordion
          title={t('Filtro por etiquetas')}
          icon={TagsIcon}
          isOpen={isLabelFilterOpen}
          onOpen={() => setIsLabelFilterOpen(prev => !prev)}
          size={AccordionSizeEnum.Xs}
        >
          <></>
        </Accordion>
        {isLabelFilterOpen &&
          labelDropdownCoords &&
          createPortal(
            <div
              className="absolute z-100 border border-neutral-20 bg-white shadow-lg"
              style={{
                top: labelDropdownCoords.top,
                left: labelDropdownCoords.left,
                width: labelDropdownCoords.width,
              }}
            >
              <Listbox
                mode="multiple"
                items={labelItems}
                onChange={setSelectedLabels}
                identifiers={{ key: 'value', value: 'value', label: 'label' }}
                selected={selectedLabels}
                noApplies
                noSave
              />
            </div>,
            document.body
          )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2" data-id="add-logos-container">
      <div className="flex justify-between gap-2">
        <div className="flex items-center gap-1">
          <Typography
            fontSize={FontSizeEnum.Lg}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Medium20}
          >
            {t('Logotipos')}:
          </Typography>
          <Typography
            fontSize={FontSizeEnum.Lg}
            color={FontColorEnum.Medium20}
            weight={FontWeightEnum.Semibold}
          >
            <Tooltip help={t('Logos Visibles (sin filtrar por etiqueta) / Totales')} grow>
              {visibleLogosCount}
              <span>/</span>
              {logos.length}
            </Tooltip>
          </Typography>
          {showWarningPresences && (
            <Tooltip help={t('Uno o varios logotipos no entran dentro del tiempo del corte')}>
              <WarningInfoIcon className="text-red-500" />
            </Tooltip>
          )}
          {showHiddenOutsideCutWarning && (
            <Tooltip
              help={
                <div className="flex flex-col gap-1">
                  <Typography fontSize={FontSizeEnum.Xs} color={FontColorEnum.Neutral}>
                    {t(
                      'Para este corte existen presencias filtradas fuera de rango que impiden guardar'
                    )}
                  </Typography>
                  {hiddenOutsideCutLabels.length > 0 && (
                    <ul className="list-disc pl-4">
                      {hiddenOutsideCutLabels.map(label => (
                        <li key={label}>
                          <Typography fontSize={FontSizeEnum.Xs} color={FontColorEnum.Neutral}>
                            {label}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              }
            >
              <span
                data-id="hidden-outside-cut-chip"
                className="flex w-fit cursor-default flex-nowrap items-center rounded-md border-[1px] border-feedback-error-20 px-2 text-basic text-feedback-error-20"
              >
                {t('Logotipos erróneos ocultos')}
              </span>
            </Tooltip>
          )}
        </div>
        <ClipTooltip handleClick={handleClickAddLogo} disabled={disabled} />
      </div>

      {drawerRect && drawingFor === 'logo' && (
        <div className="mb-5 overflow-hidden rounded-lg border-[8px] border-gray-200">
          <SelectForm
            form={form as any}
            name="logo_id"
            label={t('Logotipos')}
            hideLabelTag={true}
            {...logoSelectProps}
            handleChange={handleChangeSelect}
            additionalButtons={renderAdditionalButtons()}
          />
        </div>
      )}

      {isLoadingPreview ? (
        <div className="flex w-full justify-center">
          <LoaderIcon className="animate-spin" />
        </div>
      ) : (
        groupedItems.length > 0 && (
          <div className="overflow-x-auto">
            <div className="flex items-center justify-between gap-6">
              {getLabelsFilter()}
              <div className="flex items-center justify-end gap-6 border-t border-dotted border-gray-200 px-1 pt-4">
                <div className="min-w-0">
                  <ThumbnailSizeSlider value={thumbnailSize} onChange={setThumbnailSize} />
                </div>
                <CollapseAllButton
                  isExpanded={!allCollapsed}
                  onClick={() => setAllCollapsed(prev => !prev)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-3 py-2" data-id="logos-list-container">
              {visibleGroupedItems.map((group, i) => {
                const key = (group as any).children
                  ? (group as any).id
                  : ((group as any).logo_id ?? resolveItemId(group as any)) || String(i);
                const groupIsOutsideCut = isGroupOutsideCut<TSegmentLogoClip>(
                  group as any,
                  cutStart,
                  cutEnd
                );

                return (
                  <PresenceGroupCard
                    key={key}
                    group={group as any}
                    thumbnailSize={thumbnailSize}
                    selectedIds={selectedIds}
                    activeItemId={visibleBox?.id?.toString()}
                    onToggleGroupSelection={toggleGroupSelection}
                    onToggleItemSelection={toggleItemSelection}
                    onPreview={handlePreviewLogo}
                    onValidateGroup={handleValidateGroup}
                    onInvalidateGroup={handleInvalidateGroup}
                    onMove={handleBatchMove}
                    onDelete={handleBatchDelete}
                    itemThumbnailMap={itemThumbnailMap}
                    presenceType={PresenceTypeEnum.LOGOS}
                    isCollapsed={allCollapsed}
                    isOutsideCut={groupIsOutsideCut}
                  />
                );
              })}
            </div>
          </div>
        )
      )}

      <LogosDrawerForm
        isOpen={drawerLogoForm.isOpen}
        isMutating={isCreatingLogos}
        onClose={drawerLogoForm.close}
        onSubmit={handleSubmitLogoWrapper}
      />
    </div>
  );
}
